/**
 * WHAT: React error boundary that catches JavaScript errors in the component tree.
 * WHY: Prevents the entire DAMA application from crashing when a single component
 *      fails, showing a user-friendly error screen with recovery options instead
 *      of a blank page that would block attestation and compliance workflows.
 */

import React from 'react';

/**
 * WHAT: Class-based error boundary with fallback UI, retry, and navigation options.
 * WHY: Provides graceful degradation so that a JavaScript error in one module
 *      (e.g., a chart rendering issue) does not prevent access to other critical
 *      features like attestation or campaign management.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  /* WHAT: Sets hasError flag when a child component throws during rendering.
     WHY: Required by React error boundary API to trigger the fallback UI on the next render cycle. */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  /* WHAT: Logs error details and updates state with the full error and component stack trace.
     WHY: Captures diagnostic information for debugging; error count tracking helps detect render loops. */
  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // TODO: Send error to logging service (e.g., Sentry, LogRocket)
    // Example:
    // logErrorToService(error, errorInfo);
  }

  /* WHAT: Clears the error state so the children can attempt to re-render.
     WHY: Gives users a "Try Again" recovery path without requiring a full page reload. */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '3rem',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-exclamation-triangle" style={{
                fontSize: '2.5rem',
                color: 'white'
              }}></i>
            </div>

            <h2 style={{
              color: '#2d3748',
              marginBottom: '1rem',
              fontSize: '1.75rem',
              fontWeight: '700'
            }}>
              Oops! Something went wrong
            </h2>

            <p style={{
              color: '#718096',
              marginBottom: '2rem',
              fontSize: '1rem',
              lineHeight: '1.6'
            }}>
              We're sorry for the inconvenience. An unexpected error occurred while processing your request.
            </p>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn btn-primary"
                onClick={this.handleReset}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <i className="fas fa-redo me-2"></i>
                Try Again
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '2px solid #cbd5e0',
                  background: 'white',
                  color: '#4a5568',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#4a5568';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.color = '#4a5568';
                }}
              >
                <i className="fas fa-home me-2"></i>
                Go Home
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '2px solid #cbd5e0',
                  background: 'white',
                  color: '#4a5568',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#4a5568';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.color = '#4a5568';
                }}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Reload Page
              </button>
            </div>

            {/* Show error details in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: '2rem',
                textAlign: 'left',
                background: '#f7fafc',
                padding: '1rem',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '0.5rem'
                }}>
                  <i className="fas fa-bug me-2"></i>
                  Error Details (Development Mode)
                </summary>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#e53e3e',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  marginTop: '0.5rem'
                }}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  {this.state.errorInfo && (
                    <>
                      <br /><br />
                      <strong>Component Stack:</strong>
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </div>
              </details>
            )}

            <p style={{
              marginTop: '2rem',
              fontSize: '0.875rem',
              color: '#a0aec0'
            }}>
              If this problem persists, please contact your system administrator.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
